import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Phone, ClipboardList, FileText, Plane, Building2, LogOut, CalendarClock, AlertTriangle } from "lucide-react";
import { Link } from "react-router-dom";
import { fmtDateTime, relativeTime } from "@/lib/format";

const WIDGETS = [
  { key: "overdue_tasks",           label: "Overdue tasks",         icon: AlertTriangle, accent: "#C0392B", link: "/coordinator/tasks?filter=overdue" },
  { key: "todays_calls",            label: "Today's calls",         icon: Phone,         accent: "#D97757" },
  { key: "pending_doctor_opinions", label: "Pending doctor opinions", icon: ClipboardList, accent: "#2980B9" },
  { key: "pending_estimates",       label: "Pending estimates",     icon: FileText,      accent: "#B26B00" },
  { key: "arriving_this_week",      label: "Arriving this week",    icon: Plane,         accent: "#7A5AC2" },
  { key: "admissions_today",        label: "Admissions today",      icon: Building2,     accent: "#2A4D3E" },
  { key: "discharges_this_week",    label: "Discharges this week",  icon: LogOut,        accent: "#5C6660" },
  { key: "followups_due",           label: "Follow-ups due",        icon: CalendarClock, accent: "#2A4D3E" },
];

export default function CoordinatorWidgets({ role = "coordinator" }) {
  const [data, setData] = useState(null);
  useEffect(() => {
    (async () => {
      try { const { data } = await api.get("/widgets/coordinator"); setData(data); }
      catch {}
    })();
  }, []);
  if (!data) return null;
  return (
    <div className="mb-10" data-testid="coordinator-widgets">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-5">
        {WIDGETS.map(({ key, label, icon: Icon, accent, link }) => (
          <MetricCard key={key} label={label} value={data[key]} icon={Icon} accent={accent}
            testid={`widget-${key}`} link={link ? `/${role}${link.slice(link.indexOf('/', 1))}` : null} />
        ))}
      </div>
      {data.upcoming_tasks?.length > 0 && (
        <div className="bg-white border border-[#E8E6E1] rounded-2xl p-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <div className="font-heading font-semibold text-lg text-[#2A332C]">Upcoming tasks</div>
              <div className="text-xs text-[#8E9992]">Auto-generated by the workflow engine</div>
            </div>
            <Link to={`/${role}/tasks`} className="text-xs text-[#2A4D3E] font-semibold hover:underline" data-testid="see-all-tasks">See all →</Link>
          </div>
          <div className="space-y-2">
            {data.upcoming_tasks.slice(0, 5).map((t) => (
              <div key={t.id} className="flex items-center justify-between py-2 border-b border-[#E8E6E1] last:border-0">
                <div>
                  <div className="text-sm font-medium text-[#2A332C]">{t.title}</div>
                  <div className="text-[11px] text-[#8E9992]">{t.next_action || ""}</div>
                </div>
                <div className="text-[11px] text-[#8E9992] flex items-center gap-1"><CalendarClock size={11} /> {relativeTime(t.due_at)}</div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function MetricCard({ label, value, icon: Icon, accent, testid, link }) {
  const inner = (
    <div className="bg-white border border-[#E8E6E1] rounded-2xl p-4 hover:shadow-sm transition h-full" data-testid={testid}>
      <div className="flex items-center gap-2 mb-2">
        <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: `${accent}14`, color: accent }}>
          <Icon size={14} strokeWidth={1.8} />
        </div>
      </div>
      <div className="text-[10px] uppercase tracking-[0.14em] text-[#8E9992] font-semibold">{label}</div>
      <div className="font-heading text-2xl font-semibold text-[#2A332C] mt-1">{value ?? 0}</div>
    </div>
  );
  return link ? <Link to={link}>{inner}</Link> : inner;
}
